/**
 * @fileoverview Organisation Management Page
 *
 * Purpose: List and search all organisations on the platform.
 * URL: /admin/organisations
 * Protection: Requires super_admin role
 *
 * Features:
 * - Search by organisation name
 * - Data table with org details, member count, owner, job count
 * - Link to organisation detail page
 *
 * Dependencies:
 * - Convex: admin.organisations.list
 * - shadcn Table, Badge, Button, Input
 */

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Switch } from "@/ui/primitives/switch";
import { Search, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";

/** Shape of an organisation row from the admin.organisations.list query */
interface AdminOrgRow {
  _id: Id<"organizations">;
  _creationTime: number;
  name: string;
  slug?: string;
  workOsOrgId: string;
  memberCount: number;
  ownerName?: string;
  jobCount: number;
  masterLibraryAccess: boolean;
}

export const Route = createFileRoute("/_app/_auth/admin/_layout/organisations")(
  {
    beforeLoad: async ({ context }) => {
      const user = await context.queryClient.ensureQueryData(
        convexQuery(api.users.queries.getCurrentUser, {}),
      );
      if (!user?.isOwner) {
        throw redirect({ to: "/admin/master-library" });
      }
    },
    component: OrganisationsPage,
  },
);

function OrganisationsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);

  const queryArgs = useMemo(
    () => ({
      search: debouncedSearch || undefined,
    }),
    [debouncedSearch],
  );

  const { data: organisations } = useQuery(
    convexQuery(api.admin.organisations.list, queryArgs),
  );

  const { mutateAsync: setMasterAccess } = useMutation({
    mutationFn: useConvexMutation(
      api.admin.organisations.setMasterLibraryAccess,
    ),
  });

  const handleToggleMasterAccess = async (
    orgId: Id<"organizations">,
    enabled: boolean,
  ) => {
    try {
      await setMasterAccess({ orgId, enabled });
      toast.success(
        enabled
          ? "ARITA library access granted"
          : "ARITA library access revoked",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update access"));
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Organisations</h1>
        <p className="text-sm text-muted-foreground">
          View and manage all organisations on the platform.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Jobs</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>ARITA Library</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organisations && organisations.length > 0 ? (
              organisations.map((org: AdminOrgRow) => (
                <TableRow
                  key={org._id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/admin/organisations/$orgId",
                      params: { orgId: org._id },
                    })
                  }
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{org.name}</span>
                      {org.slug && (
                        <span className="text-xs text-muted-foreground">
                          {org.slug}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {org.memberCount}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {org.ownerName ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {org.jobCount}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(org._creationTime).toLocaleDateString()}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    className="cursor-default"
                  >
                    <Switch
                      checked={org.masterLibraryAccess}
                      onCheckedChange={(checked) =>
                        handleToggleMasterAccess(org._id, checked)
                      }
                      aria-label="Toggle ARITA library access"
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-muted-foreground"
                >
                  {organisations === undefined
                    ? "Loading..."
                    : "No organisations found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Simple debounce hook for search input.
 */
function useDebounce(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}
